// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { type ChangelogRelease, parseChangelog } from "@/lib/changelog";

/**
 * Parsed CHANGELOG.md (repo root), the single source of truth for both the sidebar
 * version label and the Changelog dialog. The root-absolute glob avoids a `../../`
 * import (forbidden by the import convention); eager + ?raw inlines the file content
 * at build time, which we parse once at module load.
 *
 * This module uses `import.meta.glob`, which only exists under Vite — keep it out of
 * anything imported by the (Vite-less) Bun test runtime. The pure parser lives in
 * `@/lib/changelog` for unit testing.
 */
const changelogFiles = import.meta.glob<string>("/CHANGELOG.md", {
  query: "?raw",
  import: "default",
  eager: true,
});

/** Releases parsed from CHANGELOG.md, newest first. */
export const CHANGELOG: ChangelogRelease[] = parseChangelog(changelogFiles["/CHANGELOG.md"] ?? "");

/** Codename for a given semver, taken from its CHANGELOG entry. `undefined` if none. */
export function releaseCodename(version: string): string | undefined {
  return CHANGELOG.find((release) => release.version === version)?.codename;
}
